import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Globe, ShieldCheck, Users, Sparkles, ArrowRight } from "lucide-react";

const values = [
  { icon: <Globe size={20} />, title: "Regional expertise", body: "Deep familiarity with public-sector procurement, aviation authorities and enterprise IT buyers across the Middle East and Central Asia." },
  { icon: <ShieldCheck size={20} />, title: "Engineering discipline", body: "Structured delivery, documented handovers and long-term support commitments on every project we sign for." },
  { icon: <Users size={20} />, title: "Vendor-neutral advice", body: "We choose technologies based on your operational requirements — not on quotas from a single manufacturer." },
  { icon: <Sparkles size={20} />, title: "Innovation without hype", body: "AI, cloud and automation are only deployed when they measurably improve safety, efficiency or revenue." },
];

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="about-page" className="pt-24 pb-16">
      <div className="max-w-[1280px] mx-auto px-6 mb-8">
        <nav className="flex items-center gap-2 text-sm text-[#a6b3c8]" style={{ fontFamily: "Sora, sans-serif" }}>
          <Link to="/" className="hover:text-white">Home</Link>
          <ChevronRight size={14} className="opacity-60" />
          <span className="text-white">About</span>
        </nav>
      </div>

      <section className="mb-16">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-3xl">
            <div className="section-eyebrow">About us</div>
            <h1 className="section-title">EmirATech Distribution L.L.C-FZ</h1>
            <p className="text-[#c8d4ea] text-lg leading-relaxed">
              EmirATech Distribution is a UAE-headquartered systems integrator and technology distributor. We help air navigation service providers, airport operators and enterprise IT teams design, procure and operate the systems that keep their business running — safely, efficiently and predictably.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-16">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="glass-card p-8">
            <div className="section-eyebrow mb-3">Our mission</div>
            <h3 className="text-white font-semibold text-xl mb-3" style={{ fontFamily: "Sora, sans-serif" }}>
              Turn complex requirements into reliable systems
            </h3>
            <p className="text-[#a6b3c8] text-[15px] leading-relaxed">
              We take responsibility for the outcome — not just the delivery of boxes and licenses. Every engagement is scoped, designed and supported to a published quality standard.
            </p>
          </div>
          <div className="glass-card p-8">
            <div className="section-eyebrow mb-3">Our approach</div>
            <h3 className="text-white font-semibold text-xl mb-3" style={{ fontFamily: "Sora, sans-serif" }}>
              Advisory, delivery and long-term support under one roof
            </h3>
            <p className="text-[#a6b3c8] text-[15px] leading-relaxed">
              We combine independent advisory with hands-on engineering, so architecture decisions and delivery reality stay aligned throughout the lifecycle.
            </p>
          </div>
          <div className="glass-card p-8">
            <div className="section-eyebrow mb-3">Our footprint</div>
            <h3 className="text-white font-semibold text-xl mb-3" style={{ fontFamily: "Sora, sans-serif" }}>
              Middle East and Central Asia
            </h3>
            <p className="text-[#a6b3c8] text-[15px] leading-relaxed">
              From our UAE base we deliver projects across the region, working with local partners for on-site presence and long-term operational support.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-16">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="mb-6 max-w-3xl">
            <div className="section-eyebrow">What matters to us</div>
            <h2 className="section-title">Values in practice</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v, i) => (
              <div key={v.title} data-testid={`about-value-${i}`} className="glass-card p-6">
                <div className="svc-icon mb-4" style={{ width: 42, height: 42 }}>{v.icon}</div>
                <h3 className="text-white text-base font-semibold mb-2" style={{ fontFamily: "Sora, sans-serif" }}>{v.title}</h3>
                <p className="text-[#a6b3c8] text-[13.5px] leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="p-8 md:p-10 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6" style={{ background: "linear-gradient(135deg, rgba(31,74,155,0.22), rgba(31,107,91,0.10))", border: "1px solid rgba(59,116,209,0.32)" }}>
            <div>
              <h3 className="text-white text-2xl font-semibold mb-1" style={{ fontFamily: "Sora, sans-serif" }}>
                Interested in working with us?
              </h3>
              <p className="text-[#a6b3c8]">Reach out — we reply within one business day.</p>
            </div>
            <Link to="/contact" className="btn-primary shrink-0">
              Contact Us <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
